import cornerstoneTools from "cornerstone-tools";
import {
  mergeAdjacentFluidRowDatums,
  replaceFluidSegmentationData,
} from "../utils/fluidManualBrush";

const BaseAnnotationTool = cornerstoneTools.importInternal(
  "base/BaseAnnotationTool"
);

const external = cornerstoneTools.external;
const draw = cornerstoneTools.importInternal("drawing/draw");
const getNewContext = cornerstoneTools.importInternal("drawing/getNewContext");

export const SEGMENTATION_ERASER_MODE = {
  AI: "ai",
  FLUID: "fluid",
};

let activeSegmentationEraserMode = SEGMENTATION_ERASER_MODE.AI;

export function setSegmentationEraserMode(mode) {
  activeSegmentationEraserMode =
    mode === SEGMENTATION_ERASER_MODE.FLUID
      ? SEGMENTATION_ERASER_MODE.FLUID
      : SEGMENTATION_ERASER_MODE.AI;
}

export function getSegmentationEraserMode() {
  return activeSegmentationEraserMode;
}

/**
 * Mode is also stored on cornerstoneTools (singleton) so it always matches the
 * running tool instance even if this module were duplicated in the bundle.
 */
function resolveSegmentationEraseMode() {
  const fromTools = cornerstoneTools.segmentationEraseMode;
  if (fromTools === SEGMENTATION_ERASER_MODE.FLUID) {
    return SEGMENTATION_ERASER_MODE.FLUID;
  }
  if (fromTools === SEGMENTATION_ERASER_MODE.AI) {
    return SEGMENTATION_ERASER_MODE.AI;
  }
  return activeSegmentationEraserMode;
}

function getFrameMetaFromElement(element) {
  const enabled = external.cornerstone.getEnabledElement(element);
  const imageId = enabled?.image?.imageId;
  const frameId = imageId?.split("?").pop();
  const frameIndexMatch = frameId?.match(/frame=(\d+)/);
  const frameIndex = frameIndexMatch ? Number(frameIndexMatch[1]) : null;

  return {
    imageId,
    frameId,
    frameIndex: Number.isInteger(frameIndex) ? frameIndex : null,
  };
}

function resolveFluidSegmentationState(element) {
  let ts = cornerstoneTools.getToolState(element, "FluidSegmentation");
  if (ts && Array.isArray(ts.data) && ts.data.length) {
    return ts;
  }

  const enabled = external.cornerstone.getEnabledElement(element);
  const imageId = enabled?.image?.imageId;
  const mgr = cornerstoneTools.globalImageIdSpecificToolStateManager;

  if (imageId && mgr && typeof mgr.saveImageIdToolState === "function") {
    try {
      const bundle = mgr.saveImageIdToolState(imageId);
      const fromBundle = bundle && bundle.FluidSegmentation;
      if (fromBundle?.data?.length) {
        return fromBundle;
      }
    } catch (e) {
      /* noop */
    }
  }

  if (!imageId) {
    return ts || null;
  }

  if (mgr && typeof mgr.getImageIdToolState === "function") {
    const byId = mgr.getImageIdToolState(imageId, "FluidSegmentation");
    if (byId && Array.isArray(byId.data) && byId.data.length) {
      return byId;
    }
  }
  if (mgr && typeof mgr.getToolState === "function") {
    const byId = mgr.getToolState(imageId, "FluidSegmentation");
    if (byId && Array.isArray(byId.data) && byId.data.length) {
      return byId;
    }
  }

  return ts || null;
}

const FLUID_ERASE_LABELS = new Set(["IRF", "SRF", "PED", "SHRM"]);

/** Extra image px added to erase radius vs visual ring — matches thick row rasterization. */
const FLUID_ERASE_GEOM_EXPAND = 1.15;

/** Min length (image px sq) kept for remnants when splitting segments */
const MIN_FLUID_SEGMENT_LEN2 = 1e-8;

/** @returns {typeof datum[]} */
function buildFluidPiece(meta, datum, ax, ay, bx, by, u, v) {
  const t0 = Math.max(0, Math.min(1, u));
  const t1 = Math.max(0, Math.min(1, v));
  if (t1 - t0 < 1e-9) return null;
  const sx = ax + (bx - ax) * t0;
  const sy = ay + (by - ay) * t0;
  const ex = ax + (bx - ax) * t1;
  const ey = ay + (by - ay) * t1;
  if ((ex - sx) * (ex - sx) + (ey - sy) * (ey - sy) < MIN_FLUID_SEGMENT_LEN2) {
    return null;
  }
  const baseLabel = datum?.label ?? meta.label;
  return {
    ...datum,
    ...meta,
    label: baseLabel,
    invalidated: true,
    start: { x: sx, y: sy },
    end: { x: ex, y: ey },
  };
}

/**
 * Remove portions of datum's line segment lying inside/overlapping the erase disc.
 * @returns {null} nothing to erase — @returns {[]} remove whole datum — @returns {datum[]} replacements
 */
function subtractCircleFromFluidSegment(datum, brush) {
  const ax = datum.start.x;
  const ay = datum.start.y;
  const bx = datum.end.x;
  const by = datum.end.y;
  const cx = brush.x;
  const cy = brush.y;
  const r = brush.radius + FLUID_ERASE_GEOM_EXPAND;

  const dx = bx - ax;
  const dy = by - ay;
  const wx = ax - cx;
  const wy = ay - cy;
  const dot = dx * dx + dy * dy;

  const meta = {
    visible: datum.visible !== false,
    active: datum.active !== false,
    color: datum.color,
    label: datum.label,
    invalidated: true,
    alpha: typeof datum.alpha === "number" ? datum.alpha : 0.3,
  };

  function distSqAt(t) {
    const px = ax + t * dx;
    const py = ay + t * dy;
    const ddx = px - cx;
    const ddy = py - cy;
    return ddx * ddx + ddy * ddy;
  }

  function inside(t) {
    return distSqAt(t) <= r * r + 1e-9;
  }

  if (dot < MIN_FLUID_SEGMENT_LEN2) {
    return inside(0) ? [] : null;
  }

  const breaks = new Set([0, 1]);
  const r2 = r * r;
  const aCoef = dot;
  const bCoef = 2 * (wx * dx + wy * dy);
  const cCoef = wx * wx + wy * wy - r2;
  let disc = bCoef * bCoef - 4 * aCoef * cCoef;
  if (disc > 1e-12) {
    const s = Math.sqrt(disc);
    const tA = (-bCoef - s) / (2 * aCoef);
    const tB = (-bCoef + s) / (2 * aCoef);
    [tA, tB].forEach((t) => {
      if (t > 1e-8 && t < 1 - 1e-8) {
        breaks.add(t);
      }
    });
  }

  const sorted = Array.from(breaks).sort((x, y) => x - y);

  /** @type {typeof datum[]} */
  const outside = [];

  const totalLen = dot > 1e-20 ? Math.sqrt(dot) : 1;

  for (let i = 0; i < sorted.length - 1; i++) {
    let u = sorted[i];
    let v = sorted[i + 1];
    if (v - u < 1e-10) continue;
    const mid = (u + v) * 0.5;
    const piece = inside(mid)
      ? null
      : buildFluidPiece(meta, datum, ax, ay, bx, by, u, v);
    if (!inside(mid) && piece) {
      outside.push(piece);
    }
  }

  if (outside.length === 0) {
    return [];
  }

  const outsideLenApprox = sorted.reduce((acc, tk, idx) => {
    if (idx === 0) return acc;
    const u = sorted[idx - 1];
    const v = tk;
    const mid = (u + v) * 0.5;
    return acc + (!inside(mid) ? (v - u) * totalLen : 0);
  }, 0);

  if (outsideLenApprox >= totalLen * (1 - 1e-4)) {
    return null;
  }

  return outside;
}

export const FLUID_ERASER_DIAMETER_MIN = 6;
export const FLUID_ERASER_DIAMETER_MAX = 120;
export const AI_SEGMENTATION_ERASER_DIAMETER_MIN = 2;
export const AI_SEGMENTATION_ERASER_DIAMETER_MAX = 100;

function clampDiameter(px, min, max, fallback) {
  if (typeof px !== "number" || !Number.isFinite(px)) return fallback;
  return Math.min(max, Math.max(min, Math.round(px)));
}

export function clampFluidEraseDiameter(px) {
  return clampDiameter(
    px,
    FLUID_ERASER_DIAMETER_MIN,
    FLUID_ERASER_DIAMETER_MAX,
    20
  );
}

/** Step fluid eraser diameter (mouse wheel); returns clamped diameter. */
export function adjustFluidEraseDiameter(current, spinY) {
  const cur = clampFluidEraseDiameter(current);
  const step = spinY > 0 ? -2 : 2;
  return clampFluidEraseDiameter(cur + step);
}

export function clampAiSegmentationEraseDiameter(px) {
  return clampDiameter(
    px,
    AI_SEGMENTATION_ERASER_DIAMETER_MIN,
    AI_SEGMENTATION_ERASER_DIAMETER_MAX,
    20
  );
}

/** Step AI segmentation eraser diameter (mouse wheel); returns clamped diameter. */
export function adjustAiSegmentationEraseDiameter(current, spinY) {
  const cur = clampAiSegmentationEraseDiameter(current);
  const step = spinY > 0 ? -2 : 2;
  return clampAiSegmentationEraseDiameter(cur + step);
}

function fluidBrushDiameterPx(toolFallback) {
  const g = cornerstoneTools.fluidEraserDiameterPx;
  if (typeof g === "number" && Number.isFinite(g) && g > 0) {
    return clampFluidEraseDiameter(g);
  }
  const bs = toolFallback?.configuration?.brushSize;
  if (typeof bs === "number" && bs > 0 && Number.isFinite(bs)) {
    return clampFluidEraseDiameter(bs);
  }
  return 20;
}

function aiBrushDiameterPx(toolFallback) {
  const g = cornerstoneTools.aiSegmentationEraserDiameterPx;
  if (typeof g === "number" && Number.isFinite(g) && g > 0) {
    return clampAiSegmentationEraseDiameter(g);
  }
  const bs = toolFallback?.configuration?.brushSize;
  if (typeof bs === "number" && bs > 0 && Number.isFinite(bs)) {
    return clampAiSegmentationEraseDiameter(bs);
  }
  return 20;
}

/** Fast reject: horizontal fluid segment vs erase disc. */
function segmentMayIntersectCircle(datum, brush) {
  if (!datum?.start || !datum?.end) return false;
  const r = brush.radius + FLUID_ERASE_GEOM_EXPAND;
  const cx = brush.x;
  const cy = brush.y;
  const y = datum.start.y;
  if (Math.abs(y - cy) > r) return false;
  const xMin = Math.min(datum.start.x, datum.end.x);
  const xMax = Math.max(datum.start.x, datum.end.x);
  return !(xMax < cx - r || xMin > cx + r);
}

/** Samples between previous and current erase centers when dragging quickly (fluid only). */
function interpolateFluidEraseStamps(prev, curr, segmentCount = 0) {
  const dx = curr.x - prev.x;
  const dy = curr.y - prev.y;
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d < 1e-7) return [];
  const rad = curr.radius || 1;
  const stride = Math.max(
    rad * 0.5,
    Math.min(8, rad / (segmentCount > 200 ? 6 : 3))
  );
  const maxStamps = segmentCount > 300 ? 4 : segmentCount > 150 ? 8 : 16;
  const stamps = [];
  let acc = stride;
  while (acc < d - stride * 0.2 && stamps.length < maxStamps) {
    const ft = Math.min(1, acc / d);
    stamps.push({
      x: prev.x + ft * dx,
      y: prev.y + ft * dy,
      radius: curr.radius,
    });
    acc += stride;
  }
  return stamps;
}

/** Keep eraser sizing in sync for fluid mode (instances may not inherit prototype.configuration changes). */
export function syncFluidEraseDiameter(px) {
  const d = clampFluidEraseDiameter(px);
  cornerstoneTools.fluidEraserDiameterPx = d;
  const ctor = cornerstoneTools.AiSegmentationEraser;
  if (ctor?.prototype?.configuration) {
    ctor.prototype.configuration = {
      ...ctor.prototype.configuration,
      brushSize: d,
    };
  }
}

/** Keep eraser sizing in sync for AI mode (instances may not inherit prototype.configuration changes). */
export function syncAiSegmentationEraseDiameter(px) {
  const d = clampAiSegmentationEraseDiameter(px);
  cornerstoneTools.aiSegmentationEraserDiameterPx = d;
  const ctor = cornerstoneTools.AiSegmentationEraser;
  if (ctor?.prototype?.configuration) {
    ctor.prototype.configuration = {
      ...ctor.prototype.configuration,
      brushSize: d,
    };
  }
}

function drawWhiteEraserRing(context, element, brush) {
  const cCenter = external.cornerstone.pixelToCanvas(element, {
    x: brush.x,
    y: brush.y,
  });
  const cEdge = external.cornerstone.pixelToCanvas(element, {
    x: brush.x + brush.radius,
    y: brush.y,
  });
  const rCanvas = Math.max(
    1,
    Math.hypot(cEdge.x - cCenter.x, cEdge.y - cCenter.y)
  );

  draw(context, (ctx) => {
    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(cCenter.x, cCenter.y, rCanvas, 0, Math.PI * 2);
    ctx.stroke();
  });
}

/** Called from OCT viewer mousemove — tool hover preview when LMB is up. */
export function notifyFluidEraseHoverImageCoords(element, imageCoords) {
  const mode = resolveSegmentationEraseMode();
  if (
    mode !== SEGMENTATION_ERASER_MODE.FLUID &&
    mode !== SEGMENTATION_ERASER_MODE.AI
  ) {
    cornerstoneTools.fluidEraseHover = null;
    return;
  }
  if (!element || !imageCoords) {
    cornerstoneTools.fluidEraseHover = null;
    return;
  }
  cornerstoneTools.fluidEraseHover = {
    element,
    x: imageCoords.x,
    y: imageCoords.y,
  };
  external.cornerstone.updateImage(element);
}

/**
 * @public
 * @class AiSegmentationEraser
 * @memberof Tools.Annotation
 * @classdesc Tool for erasing AI segmentation points using a brush-style tool
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class AiSegmentationEraser extends BaseAnnotationTool {
  constructor(props = {}) {
    const defaultProps = {
      name: "AiSegmentationEraser",
      supportedInteractionTypes: ["Mouse"],
      mouseButtonMask: 1, // Left mouse button
      configuration: {
        brushSize: 20, // Size of the brush in pixels
        brushColor: "red", // Color of the brush
        alpha: 0.9, // Transparency level for fill
        borderAlpha: 1, // Transparency level for border outline
        borderWidth: 1.5, // Width of the border in pixels
        showEraserPath: false, // Whether to show the continuous eraser path - set to false
      },
      svgCursor: null,
    };

    super(props, defaultProps);

    // Active flag to track if we're currently erasing
    this.isErasing = false;

    // Current eraser position
    this.currentEraserPoint = null;

    // Path of points for erasing (not for display)
    this.erasePath = [];

    // Last processed eraser point for continuous erasing
    this.lastProcessedEraserPoint = null;

    /** Cached fluid segment array for the active erase stroke (fluid mode). */
    this._fluidEraseDataArr = null;
    this._fluidEraseTargetLabel = null;
    this._releaseCleanup = null;

    // Bind the methods to ensure proper 'this' context
    this.preMouseDownCallback = this.preMouseDownCallback.bind(this);
    this.mouseDragCallback = this.mouseDragCallback.bind(this);
    this.mouseUpCallback = this.mouseUpCallback.bind(this);
    this.mouseMoveCallback = this.mouseMoveCallback.bind(this);
    this.completeErase = this.completeErase.bind(this);
  }

  /** Eraser does not create persistent annotation measurements. */
  addNewMeasurement() {
    return;
  }

  createNewMeasurement() {
    return null;
  }

  createNewEraserPoint(eventData) {
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      return null;
    }

    const isFluid =
      resolveSegmentationEraseMode() === SEGMENTATION_ERASER_MODE.FLUID;
    const diameter = isFluid
      ? fluidBrushDiameterPx(this)
      : aiBrushDiameterPx(this);

    return {
      x: eventData.currentPoints.image.x,
      y: eventData.currentPoints.image.y,
      radius: diameter / 2,
    };
  }

  isPointInBrush(point, brush) {
    // Calculate distance between point and brush center
    const dx = point.x - brush.x;
    const dy = point.y - brush.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Check if distance is less than brush radius
    return distance <= brush.radius;
  }

  // When the mouse is pressed, start the eraser path
  activeCallback(element, options) {
    // Reset
    this.erasePath = [];
    this.currentEraserPoint = null;
    this.lastProcessedEraserPoint = null;
    this._fluidEraseDataArr = null;
    this._fluidEraseTargetLabel = null;
    this.clearReleaseFallback();
    external.cornerstone.updateImage(element);
  }

  clearReleaseFallback() {
    if (typeof this._releaseCleanup === "function") {
      this._releaseCleanup();
    }
    this._releaseCleanup = null;
  }

  registerReleaseFallback(element) {
    this.clearReleaseFallback();
    if (!element || typeof window === "undefined") {
      return;
    }

    const finishIfNeeded = () => {
      // Let Cornerstone's own mouseUpCallback run first when it fires normally.
      window.setTimeout(() => {
        if (this.isErasing) {
          this.completeErase(element);
        }
      }, 0);
    };

    window.addEventListener("mouseup", finishIfNeeded, { once: true });
    window.addEventListener("pointerup", finishIfNeeded, { once: true });
    window.addEventListener("blur", finishIfNeeded, { once: true });

    this._releaseCleanup = () => {
      window.removeEventListener("mouseup", finishIfNeeded);
      window.removeEventListener("pointerup", finishIfNeeded);
      window.removeEventListener("blur", finishIfNeeded);
    };
  }

  isLeftButtonStillDown(eventData) {
    const nativeEvent = eventData?.event || eventData?.eventData?.event;
    if (!nativeEvent || typeof nativeEvent.buttons !== "number") {
      return true;
    }
    return (nativeEvent.buttons & 1) === 1;
  }

  preMouseDownCallback(evt) {
    const eventData = evt.detail;
    const element = eventData.element;

    // Set flag to indicate we're erasing
    this.isErasing = true;
    this.registerReleaseFallback(element);

    // Clear previous path
    this.erasePath = [];
    this.lastProcessedEraserPoint = null;

    if (resolveSegmentationEraseMode() === SEGMENTATION_ERASER_MODE.FLUID) {
      const fluidState = resolveFluidSegmentationState(element);
      this._fluidEraseDataArr = fluidState?.data || null;
      this._fluidEraseTargetLabel = cornerstoneTools.manualFluidTargetLabel
        ? String(cornerstoneTools.manualFluidTargetLabel).toUpperCase()
        : null;
    } else {
      this._fluidEraseDataArr = null;
      this._fluidEraseTargetLabel = null;
    }

    // Create the first eraser point
    const eraserPoint = this.createNewEraserPoint(eventData);
    if (!eraserPoint) {
      return false;
    }

    // Both modes use the same hollow cursor ring; only erase targets differ.
    this.currentEraserPoint = eraserPoint;

    // Process this point immediately for real-time erasing
    this.processErasePoint(element, eraserPoint);
    this.lastProcessedEraserPoint = eraserPoint;

    // Force redraw
    external.cornerstone.updateImage(element);

    return true;
  }

  // Handle mousemove event when not actively dragging
  mouseMoveCallback(evt) {
    const eventData = evt.detail;
    const element = eventData.element;

    if (this.isErasing) {
      if (!this.isLeftButtonStillDown(eventData)) {
        this.completeErase(element);
        return;
      }
      this.handleDragEvent(evt);
      return;
    }

    const eraserPoint = this.createNewEraserPoint(eventData);

    if (resolveSegmentationEraseMode() === SEGMENTATION_ERASER_MODE.FLUID) {
      if (!eraserPoint) {
        return;
      }
      this.currentEraserPoint = eraserPoint;
      external.cornerstone.updateImage(element);
      return;
    }

    if (eraserPoint) {
      this.currentEraserPoint = eraserPoint;
      external.cornerstone.updateImage(element);
    }
  }

  // Handle mouse drag events - this is the primary function for erasing while dragging
  mouseDragCallback(evt) {
    if (!this.isErasing) {
      return;
    }

    this.handleDragEvent(evt);
  }

  // Common handler for both mouseDrag and mouseMove during erasing
  handleDragEvent(evt) {
    const eventData = evt.detail;
    const element = eventData.element;

    const eraserPoint = this.createNewEraserPoint(eventData);
    if (!eraserPoint) {
      return;
    }

    this.currentEraserPoint = eraserPoint;

    const fluid =
      resolveSegmentationEraseMode() === SEGMENTATION_ERASER_MODE.FLUID;

    if (!fluid && this.lastProcessedEraserPoint) {
      const dx = eraserPoint.x - this.lastProcessedEraserPoint.x;
      const dy = eraserPoint.y - this.lastProcessedEraserPoint.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < eraserPoint.radius / 2) {
        external.cornerstone.updateImage(element);
        return;
      }
    }

    if (fluid && this.lastProcessedEraserPoint) {
      const segCount = this._fluidEraseDataArr?.length || 0;
      const samples = interpolateFluidEraseStamps(
        this.lastProcessedEraserPoint,
        eraserPoint,
        segCount
      );
      samples.forEach((s) => {
        this.processErasePoint(element, s);
      });
    }

    this.processErasePoint(element, eraserPoint);
    this.lastProcessedEraserPoint = eraserPoint;

    external.cornerstone.updateImage(element);
  }

  processFluidErasePoint(element, eraserPoint) {
    const dataArr = this._fluidEraseDataArr;
    if (!dataArr?.length) {
      return;
    }

    const manualTarget =
      this._fluidEraseTargetLabel ||
      (cornerstoneTools.manualFluidTargetLabel
        ? String(cornerstoneTools.manualFluidTargetLabel).toUpperCase()
        : "");
    if (!manualTarget) {
      return;
    }

    for (let idx = dataArr.length - 1; idx >= 0; idx--) {
      const datum = dataArr[idx];
      if (datum.visible === false || !datum.label) {
        continue;
      }
      const labelNorm = String(datum.label).toUpperCase();
      if (!FLUID_ERASE_LABELS.has(labelNorm) || labelNorm !== manualTarget) {
        continue;
      }
      if (!datum.start || !datum.end) {
        continue;
      }
      if (!segmentMayIntersectCircle(datum, eraserPoint)) {
        continue;
      }

      const replacements = subtractCircleFromFluidSegment(datum, eraserPoint);
      if (replacements === null) {
        continue;
      }

      if (replacements.length === 0) {
        dataArr.splice(idx, 1);
      } else {
        dataArr.splice(idx, 1, ...replacements);
      }
    }
  }

  // Process a single eraser point and erase any points under it
  processErasePoint(element, eraserPoint) {
    if (resolveSegmentationEraseMode() === SEGMENTATION_ERASER_MODE.FLUID) {
      this.processFluidErasePoint(element, eraserPoint);
      return;
    }

    // Get AiSegmentation tool data
    const aiSegmentationData = cornerstoneTools.getToolState(
      element,
      "AiSegmentation"
    );

    if (
      !aiSegmentationData ||
      !aiSegmentationData.data ||
      !aiSegmentationData.data.length
    ) {
      return; // No data to erase
    }

    // For each segmentation data
    aiSegmentationData.data.forEach((data) => {
      if (data.points && data.points.length) {
        // Initialize or retrieve saved original points
        if (!data.originalPoints) {
          data.originalPoints = [...data.points];
          data.pointStatus = Array(data.originalPoints.length).fill("visible");
          data.erasedPointIndices = [];
          data.erasedLines = new Set(); // Add a set to track erased lines
        }

        // Check if any visible points are under this eraser point
        let pointsErased = false;

        data.originalPoints.forEach((point, index) => {
          // Only check visible points
          if (data.pointStatus[index] === "visible") {
            // Check if point is under eraser
            if (this.isPointInBrush(point, eraserPoint)) {
              // Mark as erased
              data.pointStatus[index] = "erased";
              if (!data.erasedPointIndices.includes(index)) {
                data.erasedPointIndices.push(index);
              }
              pointsErased = true;
            }
          }
        });

        // Check for lines intersecting with the eraser
        if (data.originalPoints.length > 1) {
          for (let i = 0; i < data.originalPoints.length - 1; i++) {
            const point1 = data.originalPoints[i];
            const point2 = data.originalPoints[i + 1];

            // Only check lines between visible points
            if (
              data.pointStatus[i] === "visible" &&
              data.pointStatus[i + 1] === "visible"
            ) {
              // Check if line intersects with eraser circle
              if (this.isLineIntersectingBrush(point1, point2, eraserPoint)) {
                // Mark the line as erased
                data.erasedLines.add(`${i}-${i + 1}`);

                // Also mark both endpoints as erased
                data.pointStatus[i] = "erased";
                data.pointStatus[i + 1] = "erased";

                // Add to erased indices if not already there
                if (!data.erasedPointIndices.includes(i)) {
                  data.erasedPointIndices.push(i);
                }
                if (!data.erasedPointIndices.includes(i + 1)) {
                  data.erasedPointIndices.push(i + 1);
                }

                pointsErased = true;
              }
            }
          }
        }

        // If we erased any points, update the visible points array
        if (pointsErased) {
          // Regenerate visible points array
          data.points = data.originalPoints.filter(
            (point, index) => data.pointStatus[index] === "visible"
          );

          // Store this erase operation
          if (!data.erasedSegments) {
            data.erasedSegments = [];
          }

          // Add timestamp to track this erasing operation
          data.erasedSegments.push({
            timestamp: new Date().getTime(),
            eraserPoint: { ...eraserPoint },
          });
        }
      }
    });
  }

  // Check if a line segment intersects with the eraser brush
  isLineIntersectingBrush(point1, point2, brush) {
    // Calculate closest point on line segment to brush center
    const x1 = point1.x;
    const y1 = point1.y;
    const x2 = point2.x;
    const y2 = point2.y;
    const x3 = brush.x;
    const y3 = brush.y;

    // Calculate line length squared
    const lineLength2 = (x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1);

    // If line is a point, just check distance to that point
    if (lineLength2 === 0) {
      return this.isPointInBrush(point1, brush);
    }

    // Calculate projection of brush center onto line
    const t = Math.max(
      0,
      Math.min(1, ((x3 - x1) * (x2 - x1) + (y3 - y1) * (y2 - y1)) / lineLength2)
    );

    // Calculate closest point on line segment
    const closestX = x1 + t * (x2 - x1);
    const closestY = y1 + t * (y2 - y1);

    // Check if closest point is within brush radius
    const dx = closestX - x3;
    const dy = closestY - y3;
    const distance = Math.sqrt(dx * dx + dy * dy);

    return distance <= brush.radius;
  }

  // When the mouse button is released, complete the erasing
  mouseUpCallback(evt) {
    const eventData = evt.detail;
    const element = eventData.element;

    this.completeErase(element);
  }

  completeErase(element) {
    // If we're not erasing, exit early
    if (!this.isErasing) {
      return;
    }

    // Reset erasing flag
    this.isErasing = false;
    this.lastProcessedEraserPoint = null;
    this.clearReleaseFallback();

    if (resolveSegmentationEraseMode() === SEGMENTATION_ERASER_MODE.FLUID) {
      const enabled = external.cornerstone.getEnabledElement(element);
      const imageId = enabled?.image?.imageId;
      if (imageId && this._fluidEraseDataArr) {
        const merged = mergeAdjacentFluidRowDatums(this._fluidEraseDataArr);
        this._fluidEraseDataArr.length = 0;
        this._fluidEraseDataArr.push(...merged);
        replaceFluidSegmentationData(imageId, this._fluidEraseDataArr);
        const ts = cornerstoneTools.getToolState(element, "FluidSegmentation");
        if (ts) ts.data = this._fluidEraseDataArr;
      }
    }

    this._fluidEraseDataArr = null;
    this._fluidEraseTargetLabel = null;

    if (resolveSegmentationEraseMode() === SEGMENTATION_ERASER_MODE.AI) {
      const aiSegmentationData = cornerstoneTools.getToolState(
        element,
        "AiSegmentation"
      );

      if (
        aiSegmentationData &&
        aiSegmentationData.data &&
        aiSegmentationData.data.length
      ) {
        aiSegmentationData.data.forEach((data) => {
          if (
            data.points &&
            data.erasedPointIndices &&
            data.erasedPointIndices.length > 0
          ) {
            this.generateErasedSegmentsInfo(data, getFrameMetaFromElement(element));
          }
        });
      }
    }

    // Clear the current eraser path and current eraser point
    this.erasePath = [];
    this.currentEraserPoint = null;

    // Redraw
    external.cornerstone.updateImage(element);

    // Dispatch a custom event to notify that erasing is complete
    const erasingCompleteEvent = new CustomEvent(
      "aiSegmentationErasingComplete",
      {
        detail: {
          element: element,
          toolName: this.name,
        },
      }
    );

    // Dispatch the event on the element and window for listeners
    element.dispatchEvent(erasingCompleteEvent);
    window.dispatchEvent(erasingCompleteEvent);
  }

  // Generate information about erased segments (consecutive points)
  generateErasedSegmentsInfo(data, frameMeta = {}) {
    if (!data.originalPoints || !data.pointStatus) return;

    // Track ranges of consecutive erased points
    const segments = [];
    let currentSegment = null;

    // Iterate through all points to find consecutive erased segments
    for (let i = 0; i < data.pointStatus.length; i++) {
      if (data.pointStatus[i] === "erased") {
        if (!currentSegment) {
          // Start a new segment
          currentSegment = {
            startIndex: i,
            indices: [i],
          };
        } else {
          // Add to existing segment
          currentSegment.indices.push(i);
        }
      } else if (currentSegment) {
        // End current segment
        currentSegment.endIndex = i - 1;
        segments.push(currentSegment);
        currentSegment = null;
      }
    }

    // Handle if the last segment goes to the end
    if (currentSegment) {
      currentSegment.endIndex = data.pointStatus.length - 1;
      segments.push(currentSegment);
    }

    // Format segments in the requested format - with one point before and one point after
    const formattedSegments = segments.map((segment) => {
      // For the start point, we want one point before the first erased point
      const startPointIndex =
        segment.startIndex > 0 ? segment.startIndex - 1 : segment.startIndex;
      // For the end point, we want one point after the last erased point
      const endPointIndex =
        segment.endIndex < data.originalPoints.length - 1
          ? segment.endIndex + 1
          : segment.endIndex;

      return {
        // Start boundary point (one before the erased segment)
        start: { ...data.originalPoints[startPointIndex] },
        // End boundary point (one after the erased segment)
        end: { ...data.originalPoints[endPointIndex] },
        imageId: frameMeta.imageId,
        frameId: frameMeta.frameId,
        frameIndex: frameMeta.frameIndex,
        // Include additional metadata
        startIndex: startPointIndex,
        endIndex: endPointIndex,
        erasedCount: segment.indices.length,
      };
    });

    // Store in data object for easy access
    data.erasedSegmentsInfo = formattedSegments;

    // Log the formatted segments

    return formattedSegments;
  }

  // This method is called by cornerstone to render the tool
  renderToolData(evt) {
    const eventData = evt.detail;
    const { element, canvasContext } = eventData;

    const cornerstoneElement = external.cornerstone.getEnabledElement(element);
    if (!cornerstoneElement) {
      return;
    }

    const hover =
      cornerstoneTools.fluidEraseHover &&
      cornerstoneTools.fluidEraseHover.element === element
        ? cornerstoneTools.fluidEraseHover
        : null;

    if (resolveSegmentationEraseMode() === SEGMENTATION_ERASER_MODE.FLUID) {
      const rVis = fluidBrushDiameterPx(this) / 2;
      const brush =
        (this.isErasing ? this.currentEraserPoint : null) ||
        (hover
          ? {
              x: hover.x,
              y: hover.y,
              radius: rVis,
            }
          : null);

      if (!brush) {
        return;
      }
      const context = getNewContext(canvasContext.canvas);
      drawWhiteEraserRing(context, element, brush);
      return;
    }

    const context = getNewContext(canvasContext.canvas);
    const rVis = aiBrushDiameterPx(this) / 2;
    const brush =
      (this.isErasing ? this.currentEraserPoint : null) ||
      (hover
        ? {
            x: hover.x,
            y: hover.y,
            radius: rVis,
          }
        : null);

    if (brush) {
      drawWhiteEraserRing(context, element, brush);
    }
  }
}
